import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Delete, Edit, InsertDriveFileOutlined, Save } from "@mui/icons-material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import FilePreviewDialog from "./dialogs/FilePreviewDialog";
import { SUPPORTED_EXTENSIONS, getFileExtension } from "common/utilities";

interface PendingFileListProps {
  /** List of local files not yet uploaded */
  files: File[];
  /** Called when the list changes (delete or rename) */
  onChange: (files: File[]) => void;
}

/**
 * Displays a list of local (pending) files with the same UX as AttachmentList:
 * - Avatar with image thumbnail (for images) or file icon
 * - Inline rename (edits the File object name)
 * - Click on avatar to open FilePreviewDialog
 * - Delete button
 */
const PendingFileList: React.FC<PendingFileListProps> = ({ files, onChange }) => {
  const translations = useTranslations();

  // State for inline editing (rename)
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editName, setEditName] = useState<string>("");

  // State for preview dialog
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  // Object URLs for image thumbnails
  const [thumbnails, setThumbnails] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const urls: { [key: string]: string } = {};
    files.forEach((file) => {
      const ext = getFileExtension(file.name) ?? "";
      if (SUPPORTED_EXTENSIONS.includes(ext) || file.type.startsWith("image/")) {
        urls[file.name + file.lastModified] = URL.createObjectURL(file);
      }
    });
    setThumbnails(urls);
    return () => {
      Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [files]);

  // Focus input when entering edit mode
  const inputRefs = useRef<{ [idx: number]: HTMLInputElement | null }>({});
  useEffect(() => {
    if (editIndex !== null) {
      inputRefs.current[editIndex]?.focus();
      inputRefs.current[editIndex]?.select();
    }
  }, [editIndex]);

  // ESC cancels rename
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setEditIndex(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const startEdit = (index: number) => {
    setEditIndex(index);
    setEditName(files[index].name);
  };

  const commitEdit = (index: number) => {
    if (editName.trim() && editName !== files[index].name) {
      const updated = files.map((f, i) => {
        if (i !== index) return f;
        // Create a new File with the new name, preserving content & type
        return new File([f], editName.trim(), { type: f.type, lastModified: f.lastModified });
      });
      onChange(updated);
    }
    setEditIndex(null);
  };

  const handleDelete = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  if (files.length === 0) return null;

  return (
    <>
      <Stack component={List} direction="column" position="relative" width="100%">
        {files.map((file, index) => {
          const thumbKey = file.name + file.lastModified;
          const thumbUrl = thumbnails[thumbKey];
          const isEditing = editIndex === index;

          return (
            <ListItem key={thumbKey} dense>
              {/* Avatar — click to preview */}
              <ListItemAvatar
                onClick={() => {
                  if (!isEditing) setPreviewFile(file);
                }}
                sx={{ cursor: "pointer" }}
              >
                <Tooltip
                  title={getTranslation(translations, "attachment.preview.label") || "Anteprima"}
                  placement="top"
                >
                  <Avatar
                    sx={{ fontSize: "0.8rem" }}
                    alt={file.name}
                    src={thumbUrl}
                  >
                    {!thumbUrl && <InsertDriveFileOutlined />}
                  </Avatar>
                </Tooltip>
              </ListItemAvatar>

              <ListItemText
                sx={{
                  textOverflow: "ellipsis",
                  maxWidth: "auto",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
                primary={
                  <Box display="flex" flexDirection="column">
                    {/* Row: inline rename input + action icons */}
                    <Stack
                      component="form"
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      onSubmit={(e) => {
                        e.preventDefault();
                        commitEdit(index);
                      }}
                    >
                      <Box flex={1} minWidth={0}>
                        <input
                          ref={(el) => (inputRefs.current[index] = el)}
                          style={{
                            border: "none",
                            background: "transparent",
                            width: "100%",
                            fontSize: "16px",
                            fontWeight: 600,
                            fontFamily: "Poppins-Light",
                            outline: isEditing ? "1px solid #54bdc1" : "none",
                            borderRadius: 2,
                          }}
                          value={isEditing ? editName : file.name}
                          onChange={(e) => setEditName(e.target.value)}
                          disabled={!isEditing}
                          onBlur={() => commitEdit(index)}
                        />
                      </Box>

                      {/* Edit / Save */}
                      <Tooltip
                        title={
                          isEditing
                            ? getTranslation(translations, "pm.button.save")
                            : getTranslation(translations, "pm.button.edit")
                        }
                      >
                        <IconButton
                          size="medium"
                          sx={{ p: 0.25 }}
                          type={isEditing ? "submit" : "button"}
                          onClick={(e) => {
                            e.preventDefault();
                            isEditing ? commitEdit(index) : startEdit(index);
                          }}
                        >
                          {isEditing ? (
                            <Save fontSize="inherit" color="primary" />
                          ) : (
                            <Edit fontSize="inherit" color="primary" />
                          )}
                        </IconButton>
                      </Tooltip>

                      {/* Delete */}
                      <Tooltip title={getTranslation(translations, "settings.delete.label") || "Elimina"}>
                        <IconButton
                          size="medium"
                          sx={{ p: 0.25 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDelete(index);
                          }}
                        >
                          <Delete fontSize="inherit" color="primary" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    {/* Second row: file size */}
                    <Typography variant="body2" component="span" sx={{ color: "text.secondary" }}>
                      {(file.size / 1024).toFixed(1)} KB
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          );
        })}
      </Stack>

      {/* Local file preview dialog */}
      <FilePreviewDialog
        attachmentId={null}
        filename={previewFile?.name ?? ""}
        localFile={previewFile ?? undefined}
        onClose={() => setPreviewFile(null)}
      />
    </>
  );
};

export default PendingFileList;
